/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import { TabView, TabPanel } from 'primereact/tabview';
import { ProgressBar } from 'sharedUi/ProgressBar';
import { Button } from 'sharedUi/Button';
import EvalCaseViewDialog from './eval-case-view-dialog';
import EvalRunCompare from './eval-run-compare';
import { evalRunService } from '../../services/eval-run.service';
import { evalSuitesService } from '../../services/eval-suites.service';
import { aiModelsService } from '../../services/ai-models.service';
import { promptsService } from '../../services/prompts.service';
import { ragsService } from '../../services/rags.service';
import { evalCasesService } from '../../services/eval-cases.service';
import { valueSetsService } from '../../services/value-sets.service';
import type { EvalRun, EvalResult, EvalRunStatus, TokenParams } from '../../types';
import { EmptyState, Metric, SectionLabel, StatusChip } from '../common/panel';
import { PANEL, PRETTY, riseAt, type Tone } from '../common/ui-kit';
import AiFeedbackBar from '../ai-feedback/ai-feedback-bar';

interface EvalRunResultProps {
  runRef: string;
  title?: string;
  /** Rendered on a route rather than in a Dialog: this component owns the page
   *  identity, so it draws the back target and sets the heading at page scale.
   *  Without it, the route wrapper and this header both named the run. */
  onBack?: () => void;
}

const TERMINAL: EvalRunStatus[] = ['completed', 'partial', 'failed', 'cancelled'];

/** What to do about a result code, so an errored case says who has to act instead
 *  of only what broke. Mirrors `_4s_sdlc/ai/result_codes.py`, which is the source
 *  of truth and is test-guarded against the engine's own constants; only the
 *  remedy line is repeated here, because that is the half a reader needs. */
const RESULT_CODE_HINTS: Record<string, string> = {
  'RUN.CASE_INPUT_EMPTY': 'Fix the case: it has no question or task.',
  'RUN.CASE_INPUT_INVALID': "Fix the case: it misses a variable the prompt requires, or a guardrail refused its input.",
  'RUN.TARGET_UNAVAILABLE': "Fix the target, or point the suite elsewhere — no model was reached.",
  'RUN.MODEL_UNRESOLVED': 'Check the model row, its provider type and its credentials.',
  'RUN.EMPTY_RESPONSE': 'The provider refused or filtered this. Try another model, or reword the case.',
  'RUN.CASE_TIMEOUT': 'Raise Eval_Case_Timeout_Seconds, or find out why the target is slow.',
  'RUN.RATE_LIMITED': 'The provider throttled this after every retry. Lower the run concurrency or raise the quota.',
  'RUN.PROVIDER_FAILED': "Read the message — it carries the provider's own text and usually names the setting to fix.",
  'RUN.EXECUTION_FAILED': "Read the run's own error — this case is collateral.",
  'RUN.BUDGET_EXCEEDED': 'The run hit its token ceiling (Eval_Run_Max_Tokens) before reaching this case.',
  'RUN.CANCELLED': 'The run was cancelled before this case ran.',
  'GRADER.NO_EXPECTED_RESULT': 'Give the case an expectedResult, or use a metric that needs none.',
  'GRADER.UNKNOWN_TYPE': 'Pick a grading method the suite editor offers.',
  'GRADER.JUDGE_CALL_FAILED': "Check the metric's judge model — it resolves separately from the model under test.",
  'GRADER.JUDGE_RESPONSE_UNPARSEABLE': 'Use a judge model that follows the rubric output format, or raise Eval_Judge_Max_Tokens in App Configuration if a reasoning judge ran out of room.',
  'GRADER.OUTPUT_NOT_JSON': 'Not a fault — a json_match metric was given prose, which scores zero.',
  'GRADER.NO_METRIC_APPLIED': 'No metric could score this case — give it what they need, or drop it from the suite.',
  'GRADER.NO_GROUND_TRUTH': 'Add ground-truth context to the case, or score it with GROUNDEDNESS instead.',
  'GRADER.NO_RETRIEVAL_CONTEXT': 'The retriever returned nothing. Check the RAG version is active and indexed.',
};

/** Tool calls are stored as `[{name, arguments, …}]` on both sides of the
 *  comparison; only the names are graded, so only the names are shown. */
const toolCallNames = (calls: unknown): string[] =>
  Array.isArray(calls)
    ? calls.map((c: any) => (c && typeof c === 'object' ? String(c.name ?? '') : String(c ?? ''))).filter(Boolean)
    : [];

/** When `expected` is given, each name is marked as one the case asked for or
 *  one it did not — the distinction the grader scores. */
const ToolCallList = ({ names, expected }: { names: string[]; expected?: string[] }) => {
  if (!names.length) return <p className="mt-1 text-sm text-ink-muted">—</p>;
  const wanted = expected ? [...expected] : null;
  return (
    <ul className="mt-1 flex flex-col gap-1">
      {names.map((n, i) => {
        let extra = false;
        if (wanted) {
          const at = wanted.indexOf(n);
          if (at === -1) extra = true;
          else wanted.splice(at, 1);
        }
        return (
          <li key={`${n}-${i}`} className="flex items-center gap-1.5 text-sm">
            <code className="break-all font-mono text-xs text-ink">{n}</code>
            {extra && <StatusChip tone="warn" dot={false}>unexpected</StatusChip>}
          </li>
        );
      })}
    </ul>
  );
};

const statusTone = (status?: EvalRunStatus): Tone => {
  switch (status) {
    case 'completed': return 'success';
    case 'failed': return 'danger';
    case 'partial': return 'warn';
    case 'cancelled': return 'neutral';
    default: return 'info';
  }
};

const fmtCost = (cost?: number | null, currency?: string) => {
  if (cost === null || cost === undefined) return '—';
  const cur = currency ? `${currency} ` : '$';
  return `${cur}${cost.toFixed(cost < 1 ? 4 : 2)}`;
};
const fmtNum = (n?: number | null) => (n === null || n === undefined ? '—' : n);
const fmtMs = (ms?: number | null) =>
  ms == null ? '—' : ms >= 1000 ? `${(ms / 1000).toFixed(ms >= 10_000 ? 0 : 1)} s` : `${ms} ms`;
const fmtRate = (v?: number | null) => (v == null ? '—' : `${(v * 100).toFixed(0)}%`);
const asText = (v: unknown): string => {
  if (v === null || v === undefined || v === '') return '';
  if (typeof v === 'string') return v;
  try { return JSON.stringify(v, null, 2); } catch { return String(v); }
};

/**
 * The engine joins per-metric reasons as `KEY: text; KEY: text`
 * (`eval_run_service._grade`), which arrives as one paragraph nobody reads to
 * the end. Split it back apart — but only at a metric key this case actually
 * scored, so a `; ` inside a reason is never mistaken for a boundary.
 */
export const splitGraderReason = (
  reason: string, metrics: string[],
): Array<{ metric: string | null; text: string }> => {
  const keys = metrics.filter((k) => reason.includes(`${k}:`));
  if (!keys.length) return [{ metric: null, text: reason }];
  const re = new RegExp(`(?:^|;\\s*)(${keys.map((k) => k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')}):\\s*`, 'g');
  const marks: Array<{ key: string; at: number; after: number }> = [];
  let m: RegExpExecArray | null = re.exec(reason);
  while (m) { marks.push({ key: m[1], at: m.index, after: re.lastIndex }); m = re.exec(reason); }
  if (!marks.length) return [{ metric: null, text: reason }];
  const out: Array<{ metric: string | null; text: string }> = [];
  const lead = reason.slice(0, marks[0].at).trim();
  if (lead) out.push({ metric: null, text: lead });
  marks.forEach((mk, i) => {
    const stop = i + 1 < marks.length ? marks[i + 1].at : reason.length;
    out.push({ metric: mk.key, text: reason.slice(mk.after, stop).replace(/;\s*$/, '').trim() });
  });
  return out;
};

/** The case's own meter: what it spent and how long it took. */
const CaseStats = ({ t }: { t: TokenParams }) => {
  const bits: string[] = [];
  if (t.inputTokens != null) bits.push(`${t.inputTokens.toLocaleString()} in`);
  if (t.outputTokens != null) bits.push(`${t.outputTokens.toLocaleString()} out`);
  if (t.reasoningTokens) bits.push(`${t.reasoningTokens.toLocaleString()} reasoning`);
  if (t.cost != null) bits.push(fmtCost(t.cost, t.currency || undefined));
  if (t.totalTime) bits.push(`${(t.totalTime / 1000).toFixed(1)}s`);
  if (!bits.length) return null;
  return (
    <p className="mt-3 text-[11px] tabular-nums text-ink-muted">{bits.join('  ·  ')}</p>
  );
};

/** A score against the bar it had to clear: a thin track, filled to the score,
 *  green at or above the bar and red below it, with the bar marked. One shape for
 *  every metric, so a column of them reads at a glance. */
const ScoreMeter = ({ value, bar, wide = false }: { value: number; bar: number; wide?: boolean }) => {
  const ok = value >= bar;
  return (
    <span className="inline-flex items-center gap-2">
      <span className={`relative inline-block h-1.5 ${wide ? 'w-28' : 'w-16'} overflow-hidden rounded-full`}
            style={{ background: 'var(--t-neutral-bg, oklch(0.93 0.006 265))' }} aria-hidden>
        <span className="absolute inset-y-0 left-0 rounded-full"
              style={{ width: `${Math.max(2, Math.min(100, value * 100))}%`, background: ok ? 'var(--t-ok, oklch(0.448 0.104 156))' : 'var(--t-danger, oklch(0.494 0.176 24))' }} />
        <span className="absolute inset-y-[-2px] w-px" style={{ left: `${bar * 100}%`, background: 'var(--t-neutral-ink, oklch(0.472 0.021 265))', opacity: 0.45 }} />
      </span>
      <span className="w-9 text-right text-sm font-semibold tabular-nums"
            style={{ color: ok ? 'var(--t-ok, oklch(0.448 0.104 156))' : 'var(--t-danger, oklch(0.494 0.176 24))' }}>{value.toFixed(2)}</span>
    </span>
  );
};

/** The verdict as a shape as well as a word. */
const OutcomeMark = ({ outcome }: { outcome: string }) => {
  const [icon, fg, bg] = outcome === 'pass' ? ['pi-check', 'var(--t-ok, oklch(0.448 0.104 156))', 'var(--t-ok-bg, oklch(0.951 0.038 156))']
    : outcome === 'error' ? ['pi-exclamation-triangle', 'var(--t-warn, oklch(0.468 0.096 75))', 'var(--t-warn-bg, oklch(0.958 0.048 90))']
      : ['pi-times', 'var(--t-danger, oklch(0.494 0.176 24))', 'var(--t-danger-bg, oklch(0.949 0.032 22))'];
  return (
    <span className="flex h-7 w-7 flex-none items-center justify-center rounded-full" style={{ color: fg, background: bg }} aria-hidden>
      <i className={`pi ${icon} text-[11px] font-bold`} />
    </span>
  );
};

// One case: a single scannable line (verdict, name, score, what missed its bar)
// that opens into the question, the two answers side by side, and one row per
// metric with its reason. Re-run hits the suite's run-once endpoint.
const CaseRow = ({ res, index, suiteRef, rerunModelRef, modelIdToRef, showModel, targetOwnsModel,
  caseName, metricLabel, suiteBar, metricBars }: {
  res: EvalResult; index: number; suiteRef?: string; rerunModelRef?: string;
  modelIdToRef?: Map<string, string>; targetOwnsModel?: boolean;
  /** Only when the run used more than one. Repeating the same model on every row
   *  cost the case code — the identifier you actually scan — its width. */
  showModel?: boolean;
  /** The case's own name — what a person wrote to describe it. The code alone
   *  ("case_pocso_child…", truncated) was all the row said. */
  caseName?: string;
  metricLabel: (key: string) => string;
  /** The bars this run was scored against (eval_run.scoring). */
  suiteBar: number;
  metricBars: Record<string, number | null | undefined>;
}) => {
  const [fresh, setFresh] = useState<EvalResult | null>(null);
  const [caseOpen, setCaseOpen] = useState(false);
  // A passing case is noise until you want it. The list opens only what needs
  // reading — a failure or an error — and leaves the rest as one scannable line.
  const [open, setOpen] = useState(
    (res.outcome || (res.errorCode ? 'error' : res.passed ? 'pass' : 'fail')) !== 'pass');
  const bodyId = `evalcase-${res._id || res.caseRef || index}-body`;
  // Re-run reuses the EXACT model that ran this case: its stored modelRefId first,
  // else resolve its modelId to a row _id, else fall back to the run's model. A case
  // may have carried a per-case/slot override, so the run-level model is only a last resort.
  // A rag/agent target answers with its OWN model, recorded on the result. Passing
  // that as the re-run's model changed only the judge's fallback — the re-run was
  // then graded by a different model than the run it re-checks. Keep the run's.
  const caseModelRef = targetOwnsModel ? rerunModelRef : (res.modelRefId
    || (res.modelId ? modelIdToRef?.get(res.modelId) : undefined)
    || rerunModelRef);
  const rerun = useMutation({
    mutationFn: () => evalRunService.runSingleCase(
      String(suiteRef || res.suiteRef), String(res.caseRef), caseModelRef),
    onSuccess: (r) => setFresh(r),
  });

  const r = fresh || res;
  const outcome = r.outcome || (r.errorCode ? 'error' : r.passed ? 'pass' : 'fail');
  const errored = outcome === 'error';
  const outText = asText(r.output);
  const scores = Object.entries(r.scores || {}).map(([k, v]) => [k, Number(v)] as const);
  const barFor = (k: string) => metricBars[k] ?? suiteBar;
  const missedBar = (k: string, v: number) => (r.metricsBelowThreshold || []).includes(k) || v < barFor(k);
  const missed = scores.filter(([k, v]) => missedBar(k, v));
  const overall = typeof r.overallScore === 'number' ? r.overallScore
    : typeof r.confidenceScore === 'number' ? r.confidenceScore : null;

  return (
    <div className="ui-inset ui-rise overflow-hidden" style={riseAt(Math.min(index, 8))}>
      {/* The header IS the disclosure control. Six fully-open cases is a wall of
          prose no one reads; one line each, and open what you came for. */}
      <div role="presentation" onClick={() => setOpen((o) => !o)}
        className="flex cursor-pointer items-center gap-3 px-3 py-2.5">
        <button type="button" aria-expanded={open} aria-controls={bodyId}
          aria-label={`${open ? 'Collapse' : 'Expand'} case ${index + 1}`}
          onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
          className="ui-press flex h-5 w-5 flex-none items-center justify-center rounded text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">
          <i className={`pi ${open ? 'pi-chevron-down' : 'pi-chevron-right'} text-[10px]`} aria-hidden />
        </button>
        <OutcomeMark outcome={outcome} />

        {/* Identity truncates; the verdict never does. */}
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 items-baseline gap-2">
            <span className="flex-none text-[11px] font-medium tabular-nums text-ink-muted">Case #{index + 1}</span>
            <span className="min-w-0 truncate text-sm font-semibold text-ink" title={caseName || r.caseCode || undefined}>
              {caseName || r.caseCode || 'Case'}
            </span>
            {fresh && <StatusChip tone="info" dot={false}>re-run</StatusChip>}
          </div>
          <div className="flex min-w-0 items-center gap-2">
            {r.caseCode && caseName && (res.caseRef ? (
              <button type="button" onClick={(e) => { e.stopPropagation(); setCaseOpen(true); }}
                className="min-w-0 truncate font-mono text-[11px] text-ink-muted hover:text-indigo-600 hover:underline dark:hover:text-indigo-400"
                title={`${r.caseCode} — view eval case`} data-testid="EvalRunResult.OpenCase">
                {r.caseCode}
              </button>
            ) : <span className="min-w-0 truncate font-mono text-[11px] text-ink-muted">{r.caseCode}</span>)}
            {!caseName && r.caseCode && res.caseRef && (
              <button type="button" onClick={(e) => { e.stopPropagation(); setCaseOpen(true); }}
                className="text-[11px] text-ink-muted hover:text-indigo-600 hover:underline" data-testid="EvalRunResult.OpenCase">view case</button>
            )}
            {showModel && r.modelId && (
              <span className="hidden truncate font-mono text-[11px] text-ink-muted sm:inline" title="Model that ran this case (re-run reuses it)">· {r.modelId}</span>
            )}
          </div>
        </div>

        {/* Only what went wrong is spelled out on the closed row: a metric under
            its bar. Four identical grey "1.00" chips said nothing. */}
        <div className="hidden flex-none flex-wrap justify-end gap-1 md:flex">
          {r.consistency && (
            <span className={`ui-tone ${r.consistency.passAllK ? 'ui-tone--success'
              : r.consistency.passed ? 'ui-tone--warn' : 'ui-tone--danger'}`}
              title="Trials that passed — the case passes only when every trial does">
              Trials<span className="tabular-nums font-bold">{r.consistency.passed}/{r.consistency.trials}</span>
            </span>
          )}
          {!errored && missed.map(([k, v]) => (
            <span key={k} className="ui-tone ui-tone--danger" title={`${metricLabel(k)} ${v.toFixed(2)} — below its bar ${barFor(k).toFixed(2)}`}>
              {metricLabel(k)}<span className="tabular-nums font-bold">{v.toFixed(2)}</span>
            </span>
          ))}
        </div>
        <div className="flex flex-none items-center gap-3">
          {/* An errored case was never scored: the backend stores overallScore 0.0
              for it, and showing that read as a real zero. */}
          {!errored && overall != null && <ScoreMeter value={overall} bar={suiteBar} />}
          {errored ? (
            <StatusChip tone="warn" dot={false}>Errored</StatusChip>
          ) : outcome === 'pass' ? (
            <StatusChip tone="success" dot={false}>Passed</StatusChip>
          ) : (
            <StatusChip tone="danger" dot={false}>Failed</StatusChip>
          )}
          {(suiteRef || res.suiteRef) && (
            <Button icon={rerun.isPending ? 'pi pi-spin pi-spinner' : 'pi pi-replay'}
              rounded text severity="secondary" size="small"
              aria-label={rerun.isPending ? 'Re-running this case' : 'Re-run this case'}
              tooltip="Re-run this case" tooltipOptions={{ position: 'left' }}
              onClick={(e) => { e.stopPropagation(); rerun.mutate(); }} disabled={rerun.isPending}
              data-testid="EvalRunResult.Rerun" className="ui-press" />
          )}
        </div>
      </div>

      {rerun.isError && (
        <Message severity="error" className="mx-3 mb-3 w-auto"
                 text={(rerun.error as any)?.response?.data?.message || 'The re-run could not be started.'} />
      )}

      {open && (
        <div id={bodyId} className="border-t px-4 pb-4 pt-4" style={{ borderColor: 'var(--surface-border)' }}>
          {errored && (r.errorCode || r.errorMessage) && (
            <div className="mb-4 rounded-lg px-3 py-2.5 text-sm" style={{ background: 'var(--t-warn-bg, oklch(0.958 0.048 90))', color: 'var(--t-warn, oklch(0.468 0.096 75))' }}>
              <div className="font-medium">{r.errorMessage || r.errorCode}</div>
              <div className="mt-0.5 text-xs opacity-90">
                {r.errorCode && <span className="font-mono">{r.errorCode}</span>}
                {r.errorCode && RESULT_CODE_HINTS[r.errorCode] && <> — {RESULT_CODE_HINTS[r.errorCode]}</>}
              </div>
            </div>
          )}

          {/* The question came first for the grader and it comes first here. */}
          {asText(r.input) && (
            <div className="mb-4 min-w-0">
              <SectionLabel icon="pi pi-comment">Asked</SectionLabel>
              <p className="mt-1.5 max-w-4xl whitespace-pre-wrap break-words border-l-2 pl-3 text-[15px] font-medium leading-relaxed text-ink"
                 style={{ ...PRETTY, borderColor: 'var(--t-info, oklch(0.479 0.202 277))' }}>{asText(r.input)}</p>
            </div>
          )}

          {/* What it said next to what it should have said: two panels, so the eye
              compares them instead of reading one long column. */}
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
            <div className="min-w-0 rounded-lg border p-3" style={{ borderColor: 'var(--surface-border)', background: 'var(--surface-card)' }}>
              <SectionLabel icon="pi pi-reply">Answered</SectionLabel>
              {outText ? (
                <p className="mt-1.5 whitespace-pre-wrap break-words text-sm leading-relaxed text-ink" style={PRETTY}>{outText}</p>
              ) : (
                <p className="mt-1.5 text-sm italic" style={{ color: 'var(--t-warn, oklch(0.468 0.096 75))' }}>(model returned no text)</p>
              )}
            </div>
            <div className="min-w-0 rounded-lg border border-dashed p-3" style={{ borderColor: 'var(--surface-border)' }}>
              <SectionLabel icon="pi pi-flag">Expected</SectionLabel>
              <p className="mt-1.5 whitespace-pre-wrap break-words text-sm leading-relaxed text-gray-700 dark:text-gray-300" style={PRETTY}>{asText(r.expectedResult) || '—'}</p>
            </div>
          </div>

          {/* An agent case is graded on which tools it reached for, so the calls are
              the output. */}
          {(toolCallNames(r.toolCalls).length > 0 || toolCallNames(r.expectedToolCalls).length > 0) && (
            <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-2">
              <div className="min-w-0 rounded-lg border p-3" style={{ borderColor: 'var(--surface-border)' }}>
                <SectionLabel icon="pi pi-wrench">Tools called</SectionLabel>
                <ToolCallList names={toolCallNames(r.toolCalls)} expected={toolCallNames(r.expectedToolCalls)} />
              </div>
              <div className="min-w-0 rounded-lg border border-dashed p-3" style={{ borderColor: 'var(--surface-border)' }}>
                <SectionLabel icon="pi pi-flag">Tools expected</SectionLabel>
                <ToolCallList names={toolCallNames(r.expectedToolCalls)} />
              </div>
            </div>
          )}

          {/* Each attempt, when the run repeated the case: whether the answers
              agreed is the point of running it more than once. */}
          {!!r.trials?.length && (
            <div className="mt-5" data-testid="EvalRunResult.Trials">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <SectionLabel icon="pi pi-clone">Trials</SectionLabel>
                <span className="text-xs text-ink-muted">
                  The case passes only when every trial passes · scores above are the trials&rsquo; mean
                  {r.consistency?.scoreSpread != null && <> · spread {r.consistency.scoreSpread.toFixed(2)}</>}
                </span>
              </div>
              <ol className="mt-2 divide-y rounded-lg border" style={{ borderColor: 'var(--surface-border)' }}>
                {r.trials.map((t) => (
                  <li key={t.trial} className="grid grid-cols-[3.5rem_5.5rem_1fr_auto] items-start gap-3 px-3 py-2"
                      style={{ borderColor: 'var(--surface-border)' }}>
                    <span className="text-xs tabular-nums text-ink-muted">#{t.trial}</span>
                    {t.errorCode
                      ? <StatusChip tone="warn" dot={false}>Errored</StatusChip>
                      : <StatusChip tone={t.passed ? 'success' : 'danger'} dot={false}>{t.passed ? 'Passed' : 'Failed'}</StatusChip>}
                    <p className="min-w-0 line-clamp-2 whitespace-pre-wrap break-words text-sm text-gray-700 dark:text-gray-300"
                       title={t.errorMessage || asText(t.output) || undefined}>
                      {t.errorCode ? (t.errorMessage || t.errorCode) : (asText(t.output) || '(no text)')}
                    </p>
                    <span className="whitespace-nowrap text-xs tabular-nums text-ink-muted">
                      {t.overallScore != null && <>{t.overallScore.toFixed(2)} · </>}{fmtMs(t.totalTime)}
                    </span>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {/* One row per metric: its name, its score against its bar, and the
              judge's reason. A score's direction is not always obvious from its name; the
              direction is stated once, because it is the same for every metric. */}
          {r.graderReason && (
            <div className="mt-5">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <SectionLabel icon="pi pi-verified">Why it scored that way</SectionLabel>
                <span className="text-xs text-ink-muted">1.00 is best on every metric · the mark on each bar is its pass line</span>
              </div>
              <dl className="mt-2 divide-y rounded-lg border" style={{ borderColor: 'var(--surface-border)' }}>
                {splitGraderReason(r.graderReason, [...Object.keys(r.scores || {}), 'BELOW THRESHOLD'])
                  .filter((part) => part.metric !== 'BELOW THRESHOLD').map((part, k) => {
                  const score = part.metric ? r.scores?.[part.metric] : undefined;
                  return (
                    <div key={part.metric || k} style={{ borderColor: 'var(--surface-border)' }}
                         className="grid grid-cols-1 gap-x-5 gap-y-1 px-3 py-2.5 sm:grid-cols-[15rem_1fr]">
                      <dt className="min-w-0">
                        {part.metric ? (
                          <div className="flex flex-col gap-1">
                            <span className="text-sm font-medium text-ink" title={part.metric}>{metricLabel(part.metric)}</span>
                            {score != null && <ScoreMeter value={Number(score)} bar={barFor(part.metric)} wide />}
                          </div>
                        ) : (
                          <span className="text-sm font-medium text-ink">Grader</span>
                        )}
                      </dt>
                      <dd className="min-w-0 whitespace-pre-wrap break-words text-sm leading-relaxed text-gray-700 dark:text-gray-300" style={PRETTY}>{part.text}</dd>
                    </div>
                  );
                })}
              </dl>
            </div>
          )}

          {r.tokenParams && <CaseStats t={r.tokenParams} />}

          {/* A dispute, not a rating: the reader is telling us the GRADE is
              wrong — the case expected the wrong thing, or the judge misread a
              good answer. That is how bad eval cases get found, and it is the
              one AI surface where the feedback is about our own yardstick. */}
          <AiFeedbackBar
            className="mt-5 border-t pt-3"
            componentType="EVAL"
            componentCode={r.caseRef ? `eval:${r.caseRef}` : undefined}
            componentId={String(r._id || r.caseRef || '')}
            componentVersion={caseModelRef || undefined}
            runtimeContext={{
              evalCode: r.caseCode || (r.caseRef ? String(r.caseRef) : null),
              input: r.input ?? null,
              expectedOutput: r.expectedResult ?? null,
              actualOutput: r.output ?? null,
              evalScore: r.overallScore ?? null,
              outcome,
              caseRef: r.caseRef ? String(r.caseRef) : null,
              scores: r.scores || null,
              errorCode: r.errorCode || null,
            }}
            meta={showModel ? caseModelRef : undefined}
            testId="EvalCase.Feedback"
          />
        </div>
      )}

      <EvalCaseViewDialog caseRef={res.caseRef} visible={caseOpen} onHide={() => setCaseOpen(false)} />
    </div>
  );
};

const EvalRunResult = ({ runRef, title, onBack }: EvalRunResultProps) => {
  const queryClient = useQueryClient();
  const { data, isLoading, isError, error } = useQuery<EvalRun>({
    queryKey: ['eval-run', runRef],
    queryFn: () => evalRunService.getRun(runRef),
    enabled: !!runRef,
    refetchInterval: (query) => TERMINAL.includes(query.state.data?.status as EvalRunStatus) ? false : 2000,
  });

  const status = data?.status;
  const inProgress = status === 'pending' || status === 'running';

  // Cancelling is cooperative: cases already in flight finish (their tokens are
  // spent either way), queued ones are skipped. The poll above picks up the new
  // status, so nothing is optimistically rewritten here.
  const cancel = useMutation({
    mutationFn: () => evalRunService.cancelRun(runRef),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['eval-run', runRef] }),
  });

  const { data: results = [] } = useQuery<EvalResult[]>({
    queryKey: ['eval-run-results', runRef],
    queryFn: () => evalRunService.getRunResults(runRef),
    enabled: !!runRef && !!status,
    refetchInterval: () => (inProgress ? 2000 : false),
  });

  // Each row names its case, not just its code: one lookup for the whole run.
  const caseIds = useMemo(() => [...new Set(results.map((x) => x.caseRef).filter(Boolean).map(String))].sort(), [results]);
  const { data: caseNames } = useQuery({
    queryKey: ['eval-cases', 'names', caseIds.join(',')],
    queryFn: async () => new Map((await evalCasesService.list({ ids: caseIds.join(','), page_size: caseIds.length }))
      .data.map((c: any) => [String(c._id), c.name as string])),
    enabled: caseIds.length > 0,
    staleTime: 5 * 60 * 1000,
  });
  // Metric names as people read them ("Answer correctness"), from the value set
  // the suite form uses — not a copy of it here.
  const { data: metricVS } = useQuery({
    queryKey: ['value-sets', 'evalMetric'],
    queryFn: () => valueSetsService.getByKey('evalMetric', 'en'),
    staleTime: 5 * 60 * 1000,
  });
  const metricLabel = useMemo(() => {
    const byCode = new Map((metricVS?.items || []).map((i) => [i.code, i.label]));
    return (key: string) => byCode.get(key) || key.charAt(0) + key.slice(1).toLowerCase().replace(/_/g, ' ');
  }, [metricVS]);

  // The results poll stops with the run's; its last tick can predate the final
  // case writes. Read them once more when the run finishes.
  useEffect(() => {
    if (status && TERMINAL.includes(status)) {
      queryClient.invalidateQueries({ queryKey: ['eval-run-results', runRef] });
    }
  }, [status, runRef, queryClient]);

  // #3 — resolve model + target-prompt names for the run header.
  const { data: modelsResp } = useQuery({
    queryKey: ['ai-models', 'names'],
    queryFn: () => aiModelsService.list({ page: 1, page_size: 100 }),
    staleTime: 5 * 60 * 1000,
  });
  const { data: promptsResp } = useQuery({
    queryKey: ['prompts', 'names'],
    queryFn: () => promptsService.list({ page: 1, page_size: 100 }),
    staleTime: 5 * 60 * 1000,
  });
  const modelRow = useMemo(
    () => (modelsResp?.data || []).find((m: any) => m.modelId === data?.modelId),
    [modelsResp, data?.modelId]
  );
  // A rag/agent/model target answers with its own configured model, so the run has
  // no model of its own. "—" read as missing data; name the reason instead.
  const targetOwnsModel = ['rag', 'agent', 'model'].includes(data?.targetRef?.type as string);
  const modelLabel = modelRow
    ? modelRow.displayName
    : (data?.modelId || (targetOwnsModel ? `set by the ${data?.targetRef?.type}` : '—'));
  const rerunModelRef = modelRow?._id;                       // run-level model, the re-run fallback
  // modelId (string) -> unique row _id, so a case whose result predates modelRefId
  // can still resolve back to the exact model that ran it.
  const modelIdToRef = useMemo(() => {
    const m = new Map<string, string>();
    (modelsResp?.data || []).forEach((row: any) => {
      if (row.modelId && !m.has(row.modelId)) m.set(row.modelId, row._id);
    });
    return m;
  }, [modelsResp]);
  const targetPromptId = data?.targetRef?.type === 'prompt' ? data?.targetRef?.id : null;
  // Resolve the target prompt's NAME. Fetch it directly (the loaded list may be
  // scoped/paged and not contain it); fall back to the list, then the raw id.
  const { data: targetPrompt } = useQuery({
    queryKey: ['prompt', targetPromptId],
    queryFn: () => promptsService.getById(String(targetPromptId)),
    enabled: !!targetPromptId,
    staleTime: 5 * 60 * 1000,
  });
  const targetPromptName = !targetPromptId ? null
    : ((targetPrompt as any)?.name
       || (promptsResp?.data || []).find((x: any) => x._id === targetPromptId)?.name
       || targetPromptId);

  // Every other target family — rag, agent, model, function — used to fall
  // through to "case input (direct)", which read as if the run had no target at
  // all. Name whatever the run actually points at.
  const targetRagId = data?.targetRef?.type === 'rag' ? data?.targetRef?.id : null;
  const { data: targetRag } = useQuery({
    queryKey: ['rag', targetRagId],
    queryFn: () => ragsService.getById(String(targetRagId)),
    enabled: !!targetRagId,
    staleTime: 5 * 60 * 1000,
  });
  const targetLabel = (() => {
    const ref = data?.targetRef;
    if (!ref?.type || !ref?.id) return 'case input (direct)';
    if (ref.type === 'prompt') return `Prompt "${targetPromptName}"`;
    const version = ref.version != null ? ` v${ref.version}` : '';
    if (ref.type === 'rag') return `RAG "${(targetRag as any)?.ragCode || targetRag?.name || ref.id}"${version}`;
    if (ref.type === 'model') return `Model "${(modelsResp?.data || []).find((m: any) => m._id === ref.id)?.displayName || ref.id}"`;
    return `${ref.type} "${ref.id}"${version}`;
  })();

  // #judge — grader + judge model for the run. New runs snapshot it on the run doc;
  // for runs created before that, fall back to the suite's current grader.
  const { data: suiteForGrader } = useQuery({
    queryKey: ['eval-suite-grader', data?.suiteRef],
    queryFn: () => evalSuitesService.getById(String(data?.suiteRef)),
    enabled: !!data?.suiteRef && !data?.grader,
    staleTime: 5 * 60 * 1000,
  });
  const grader = data?.grader || (suiteForGrader as any)?.grader || null;
  const graderType: string | null = grader?.type || null;
  const isJudge = graderType === 'llm_judge';
  // grader.model may be stored as the model's _id (form-created) or its modelId
  // string (older) — match either so we show the display name, never a raw id.
  const judgeModelRow = useMemo(
    () => (modelsResp?.data || []).find((m: any) => m.modelId === grader?.model || m._id === grader?.model),
    [modelsResp, grader?.model]
  );
  // Judge model: the grader's own model if set, else the run's model does the judging.
  const judgeLabel = !isJudge
    ? null
    : grader?.model
      ? (judgeModelRow ? judgeModelRow.displayName : grader.model)
      : `same as run model (${modelLabel})`;

  if (isLoading) {
    // The run's own shape — identity line, then the summary metrics — so the
    // panel holds its height and nothing jumps when the numbers land.
    return (
      <div className={`${PANEL} p-4`} aria-busy="true" aria-label="Loading run">
        <div className="mb-4 flex items-center gap-2">
          <span className="ui-skel h-4 w-24 !rounded-md" />
          <span className="ui-skel h-[18px] w-20" />
        </div>
        <div className="grid grid-cols-2 gap-x-5 gap-y-4 sm:grid-cols-4">
          {[0, 1, 2, 3].map((k) => (
            <span key={k} className="block">
              <span className="ui-skel h-2 w-14" />
              <span className="ui-skel mt-2 h-4 w-16 !rounded-md" />
            </span>
          ))}
        </div>
      </div>
    );
  }
  if (isError || !data) {
    return <Message severity="error" text={(error as any)?.message || 'Failed to load eval run'} className="w-full" />;
  }

  const summary = data.summary || {};
  // The bars this run was scored against — snapshotted on the run, so an edited
  // suite does not recolour old results. Older runs predate the snapshot.
  const suiteBar = data.scoring?.passThreshold ?? 0.7;
  const metricBars: Record<string, number | null | undefined> = Object.fromEntries(
    (data.scoring?.metrics || []).map((m) => [m.key, m.passThreshold]));
  // A per-case model is worth a column only when the cases differ; otherwise the
  // run header already named it.
  const mixedModels = new Set(results.map((r) => r.modelId).filter(Boolean)).size > 1;
  // A run scored by named metrics, as opposed to one legacy grader. OVERALL is the
  // engine's own key for the single-grader case, so it does not count as one.
  const scoredByMetrics = Object.keys(summary.metricSummary || {}).some((k) => k !== 'OVERALL');

  // Live progress while the run is in flight. Prefer the run's own counter; for
  // runs created before that field existed, fall back to how many results streamed.
  const prog = data.progress || {};
  const progTotal = prog.total ?? summary.total ?? 0;
  const progDone = prog.completed ?? (inProgress ? results.length : progTotal);
  const progPct = progTotal > 0 ? Math.min(100, Math.round((progDone / progTotal) * 100)) : 0;

  return (
    <div className={`${PANEL} p-4`}>
      {/* Persistent identity header — the run's id + status stay visible across tabs */}
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              aria-label="Back to eval runs"
              className="ui-press -ml-1 flex h-10 w-10 flex-none items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200"
            >
              <i className="pi pi-arrow-left text-sm" aria-hidden />
            </button>
          )}
          <h3 className={`${onBack ? 'text-xl' : 'text-[15px]'} font-semibold tracking-[-0.01em] text-ink`}>{title || 'Eval Run'}</h3>
          {data.evalRunId && <span className="font-mono text-xs text-ink-muted">{data.evalRunId}</span>}
          <StatusChip tone={statusTone(status)}>{status}</StatusChip>
          {inProgress && <ProgressSpinner style={{ width: '16px', height: '16px' }} strokeWidth="6" />}
          {data.suiteVersion != null && (
            <span className="text-xs tabular-nums text-ink-muted">suite v{data.suiteVersion}</span>
          )}
        </div>
        {inProgress && (
          <span className="ui-press inline-flex">
            <Button
              label={cancel.isPending ? 'Cancelling…' : 'Cancel run'}
              icon={cancel.isPending ? 'pi pi-spin pi-spinner' : 'pi pi-times'}
              severity="danger"
              outlined
              size="small"
              disabled={cancel.isPending}
              onClick={() => cancel.mutate()}
              data-testid="EvalRun.Cancel"
            />
          </span>
        )}
      </div>

      {cancel.isError && (
        <Message severity="error" className="w-full mb-2"
                 text={(cancel.error as any)?.response?.data?.message || 'Could not cancel this run.'} />
      )}

      {/* Live progress bar — visible only while the run is pending/running */}
      {inProgress && progTotal > 0 && (
        <div className="mb-3" data-testid="EvalRun.Progress">
          <div className="mb-1.5 flex items-center justify-between text-xs text-ink-muted">
            <span className="tabular-nums">Running… {progDone} / {progTotal} cases</span>
            <span className="tabular-nums font-semibold">{progPct}%</span>
          </div>
          <ProgressBar value={progPct} showValue={false} style={{ height: '6px' }} />
        </div>
      )}

      <TabView>
        {/* Tab 1 — the RUN: what it ran, and how it scored overall */}
        <TabPanel
          header={
            <div className="flex items-center gap-2" data-testid="EvalRun.Tab.Run">
              <i className="pi pi-info-circle" />
              <span>Eval Run</span>
            </div>
          }
        >
          {/* #3 — run header: what model + prompt this run used, and how it was graded */}
          <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-ink-muted">
            <span>Model: <span className="font-medium text-gray-800 dark:text-gray-200">{modelLabel}</span></span>
            <span>·</span>
            <span>Runs against: <span className="text-gray-800 dark:text-gray-200 font-medium">
              {targetLabel}
            </span></span>
            {/* The legacy `grader` block describes a whole run with one method, and
                defaults to llm_judge. On a suite with named metrics that is simply
                untrue — each metric carries its own method and its own judge — and
                the breakdown below already says so per metric. Show it only for the
                single-grader runs it actually describes. */}
            {!scoredByMetrics && graderType && (
              <><span>·</span><span>Grader: <span className="text-gray-800 dark:text-gray-200 font-medium">{graderType}</span></span></>
            )}
            {!scoredByMetrics && isJudge && (
              <><span>·</span><span>Judged by: <span className="text-gray-800 dark:text-gray-200 font-medium">{judgeLabel}</span></span></>
            )}
            {data.suiteCode && <><span>·</span><span>Suite: <span className="font-mono">{data.suiteCode}</span></span></>}
          </div>

          {status === 'failed' && data.error && <Message severity="error" text={data.error} className="w-full mb-2" />}
          {summary.verdictNote ? (
            <Message severity="warn" text={summary.verdictNote} className="w-full mb-2" data-testid="EvalRun.VerdictNote" />
          ) : status === 'partial' && (
            <Message severity="warn" text="Finished with one or more cases that could not be scored (errored)." className="w-full mb-2" />
          )}

          <div className="grid grid-cols-2 gap-x-5 gap-y-4 sm:grid-cols-4 xl:grid-cols-8">
            <Metric label="Passed" value={fmtNum(summary.passed)} tone={summary.passed ? 'success' : undefined} />
            <Metric label="Failed" value={fmtNum(summary.failed)} tone={summary.failed ? 'danger' : undefined} />
            <Metric label="Errored" value={fmtNum(summary.errored)} tone={summary.errored ? 'warn' : undefined} />
            <Metric label="Total" value={fmtNum(summary.total)} />
            <Metric label="Pass rate" value={summary.passRate == null ? '—' : `${(summary.passRate * 100).toFixed(1)}%`} />
            <Metric label="Overall" value={summary.overallScore == null ? '—' : `${(summary.overallScore * 100).toFixed(0)}%`} />
            <div className="min-w-0">
              <div className="text-[11px] font-medium uppercase tracking-[0.05em] text-ink-muted">Result</div>
              <div className="mt-1">
                {summary.passFail
                  ? <StatusChip tone={summary.passFail === 'PASS' ? 'success' : 'danger'}>{summary.passFail}</StatusChip>
                  : summary.verdictNote
                    ? <StatusChip tone="warn">Inconclusive</StatusChip>
                    : <span className="text-[15px] font-semibold text-ink">—</span>}
              </div>
            </div>
            <Metric label="Total cost" value={fmtCost(summary.totalCost, summary.currency)} />
          </div>

          {/* Speed and spend, and — when cases ran more than once — how reliably
              they pass. Hidden until a run has them (older runs never will). */}
          {(summary.latencyP95Ms != null || summary.costPerCase != null || summary.consistency) && (
            <div className="mt-4 grid grid-cols-2 gap-x-5 gap-y-4 border-t pt-4 sm:grid-cols-4 xl:grid-cols-8"
                 style={{ borderColor: 'var(--surface-border)' }} data-testid="EvalRun.SpeedCost">
              <Metric label="Latency p50" value={fmtMs(summary.latencyP50Ms)} />
              <Metric label="Latency p95" value={fmtMs(summary.latencyP95Ms)}
                tone={summary.latencyBudgetExceeded ? 'danger' : undefined}
                hint={summary.latencyBudgetMs ? `Budget ${fmtMs(summary.latencyBudgetMs)}` : undefined} />
              <Metric label="Cost / case" value={fmtCost(summary.costPerCase, summary.currency)} />
              {summary.consistency && (
                <>
                  <Metric label={`pass@${summary.consistency.trials}`} value={fmtRate(summary.consistency.passAtKRate)}
                    hint="Right at least once" />
                  <Metric label={`pass^${summary.consistency.trials}`} value={fmtRate(summary.consistency.passAllKRate)}
                    hint="Right every time" />
                  <Metric label="Flaky cases" value={fmtNum(summary.consistency.flakyCases)}
                    tone={summary.consistency.flakyCases ? 'warn' : undefined} hint="Passed some trials, not all" />
                </>
              )}
            </div>
          )}

          {/* Per-metric averages across the run's scored cases */}
          {summary.metricSummary && Object.keys(summary.metricSummary).length > 0 && (
            <div className="mt-5">
              <SectionLabel icon="pi pi-chart-bar">Metric breakdown</SectionLabel>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {Object.entries(summary.metricSummary).map(([k, v]) => (
                  <span key={k} className="ui-tone ui-tone--info">
                    {k}<span className="tabular-nums font-bold">{(Number(v) * 100).toFixed(0)}%</span>
                  </span>
                ))}
              </div>
            </div>
          )}
        </TabPanel>

        {/* Tab 2 — the RESULTS: one row per case */}
        <TabPanel
          header={
            <div className="flex items-center gap-2" data-testid="EvalRun.Tab.Results">
              <i className="pi pi-list" />
              <span>Results{results.length ? ` (${results.length})` : ''}</span>
            </div>
          }
        >
          {results.length > 0 ? (
            <div className="flex flex-col gap-2">
                  {results.map((r, i) => (
                <CaseRow key={r._id || r.caseRef || i} res={r} index={i}
                         suiteRef={data.suiteRef} rerunModelRef={rerunModelRef}
                         modelIdToRef={modelIdToRef} showModel={mixedModels} targetOwnsModel={targetOwnsModel}
                         caseName={caseNames?.get(String(r.caseRef))} metricLabel={metricLabel}
                         suiteBar={suiteBar} metricBars={metricBars} />
              ))}
            </div>
          ) : inProgress ? (
            <EmptyState icon="pi pi-hourglass" title="Waiting for the first case" hint="Results stream in as each case finishes." />
          ) : (
            <EmptyState icon="pi pi-inbox" title="No case-level results" hint="This run recorded a summary but no individual cases." />
          )}
        </TabPanel>

        {/* Tab 3 — COMPARE: which cases changed against an earlier run of the suite */}
        {!inProgress && (
          <TabPanel
            header={
              <div className="flex items-center gap-2" data-testid="EvalRun.Tab.Compare">
                <i className="pi pi-clone" />
                <span>Compare</span>
              </div>
            }
          >
            <EvalRunCompare runRef={data._id || runRef} suiteRef={data.suiteRef} />
          </TabPanel>
        )}
      </TabView>
    </div>
  );
};

export default EvalRunResult;
